import { Phone, Mail, MapPin, ShieldCheck, Check, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import { CONTACT } from "@/content";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { RomaniaMap } from "./RomaniaMap";

const Channel = ({ icon: Icon, label, value, href, testId }) => (
  <a href={href} data-testid={testId} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
    className="group flex items-center gap-5 border border-white/10 bg-[#0D0D10] p-5 hover:border-red-600/60 transition-colors">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-red-950/50 border border-red-700/50"><Icon className="h-5 w-5 text-red-500" strokeWidth={1.5} /></span>
    <span className="min-w-0 flex-1">
      <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{label}</span>
      <span className="block text-white truncate mt-0.5">{value}</span>
    </span>
    <ArrowUpRight className="h-4 w-4 text-zinc-500 transition-[color,transform] group-hover:text-red-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
  </a>
);

export const Hub = () => {
  const { t } = useLang();
  const h = t.hub;
  return (
    <section id="hub" data-testid="corporate-hub-section" className="relative py-28 lg:py-40 bg-[#08080A]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
        <Reveal><Eyebrow>{h.eyebrow}</Eyebrow><SectionTitle className="mt-6 max-w-4xl">{h.title}</SectionTitle></Reveal>
        <div className="mt-16 grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7 min-w-0">
            <div className="h-full border border-white/10 bg-[#0D0D10] p-6 sm:p-8">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-display font-bold uppercase text-2xl text-white">{h.mapTitle}</h3>
                <span className="hidden sm:inline font-mono text-[11px] text-zinc-400 tracking-widest">RO · 45.41°N 23.37°E</span>
              </div>
              <div className="mt-6"><RomaniaMap /></div>
              <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-300" data-testid="coverage-legend">
                <li className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-red-600 border-2 border-white" />{h.legend[0]}</li>
                <li className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-red-500/50" />{h.legend[1]}</li>
                <li className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-zinc-400" />{h.legend[2]}</li>
              </ul>
            </div>
          </Reveal>
          <div className="lg:col-span-5 min-w-0 flex flex-col gap-6">
            <Reveal delay={0.08}>
              <div className="border border-red-700/40 bg-[linear-gradient(135deg,#2a0707_0%,#0D0D10_70%)] p-6 sm:p-8" data-testid="credentials-card">
                <div className="flex items-center gap-3"><ShieldCheck className="h-6 w-6 text-red-500" strokeWidth={1.5} /><h3 className="font-display font-bold uppercase text-2xl text-white">{h.credTitle}</h3></div>
                <dl className="mt-6">
                  {h.creds.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 py-3 border-b border-white/10">
                      <dt className="font-mono text-xs uppercase tracking-widest text-zinc-400">{k}</dt>
                      <dd className="text-sm text-white text-right" data-testid={`credential-${k.toLowerCase().replace(/[^a-z]/g, "")}`}>{v}</dd>
                    </div>
                  ))}
                </dl>
                <ul className="mt-6 space-y-2.5">
                  {h.compliance.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm text-zinc-200"><Check className="h-4 w-4 mt-0.5 text-red-500 shrink-0" />{c}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.16} className="flex flex-col gap-3" >
              <p id="contact" className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400">{h.channels}</p>
              {CONTACT.phones.map((p, i) => <Channel key={p.href} icon={Phone} label={h.call} value={p.label} href={p.href} testId={`contact-phone-link-${i + 1}`} />)}
              {CONTACT.emails.map((e) => <Channel key={e} icon={Mail} label={h.write} value={e} href={`mailto:${e}`} testId={`contact-email-link-${e.split("@")[0]}`} />)}
              <Channel icon={MapPin} label={h.visit} value={CONTACT.address} href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONTACT.mapsQuery)}`} testId="contact-address-link" />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
